// Castintech dashboard — Kararlar (Customer Decision Timeline) view modülü.
// Faz 22 PR-C — Vault decision dosyalari timeline view'i.
// Backend: GET /castintech-api/decisions/all (Faz 22 PR-C — bu PR).
// SSE: audit.new + title='customer-decision' (frontend filter) — view açıkken
// canlı invalidate; backend Faz 16 PR-G hat'i wire'lanirsa otomatik tetiklenir.
//
// State erişimi: state.js direct import (state.kararRows, state.kararMeta).
// Utils: utils.js (escHtml/escAttr/$).
// Api: apiFetch (api.js merkezi error boundary).
//
// Kart şeması: 72px tarih kolonu + body (title + client + class badge +
// summary + owner + status badge). Faz 22 PR-B faturalar paterni mirror'i;
// fark: kanban yerine timeline (events not states), date DESC sıralı tek
// kolon list-style. Drag-drop yok, inline render (Karpathy "Surgical Changes").

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import type { DecisionRow } from '../../types/api';

const CLASS_LABELS: Record<string, string> = {
  pricing: 'Fiyat',
  scope: 'Kapsam',
  timing: 'Zamanlama',
  strategic: 'Stratejik',
  renewal: 'Yenileme',
  'churn-response': 'Churn',
};

const STATUS_LABELS: Record<string, string> = {
  proposed: 'Önerildi',
  approved: 'Onaylı',
  rejected: 'Reddedildi',
  implemented: 'Uygulandı',
  'rolled-back': 'Geri Alındı',
};

interface DecisionTimelineResponse {
  meta: { count: number; generated_at: string; source?: string };
  decisions: DecisionRow[];
}

export async function loadKararlar(): Promise<void> {
  try {
    const r = await apiFetch('/castintech-api/decisions/all');
    if (!r.ok) throw new Error('decisions HTTP ' + r.status);
    const data = (await r.json()) as DecisionTimelineResponse;
    state.kararRows = Array.isArray(data.decisions) ? data.decisions : [];
    state.kararMeta = data.meta || null;
    if (state.currentPage === 'kararlar') renderKararlar();
  } catch (e) {
    console.warn('loadKararlar failed:', (e as Error).message);
  }
}

// Tarih kolonu için 'GG Ay' (örn '15 Nis') — date DESC sıralı liste, gün +
// ay hızlı tarama için yeterli, yıl sub-header'da.
const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

function formatDate(iso: string): { day: string; month: string; year: string } {
  if (!iso) return { day: '—', month: '', year: '' };
  // YYYY-MM-DD or full ISO; manual parse safer than new Date for tz-stable.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { day: iso.slice(0, 10), month: '', year: '' };
  const yy = m[1];
  const mo = parseInt(m[2], 10);
  const dd = String(parseInt(m[3], 10));
  return {
    day: dd,
    month: TR_MONTHS[mo - 1] || '',
    year: yy,
  };
}

export function renderKararlar(): void {
  const root = $('kararlar-content');
  if (!root) return;

  const sub = $('kararlar-sub');
  if (sub) {
    const cnt = state.kararRows ? state.kararRows.length : 0;
    sub.textContent = state.kararMeta
      ? `${cnt} karar · tarih DESC timeline (vault-files)`
      : 'yükleniyor...';
  }

  if (!state.kararRows || state.kararRows.length === 0) {
    // Faz 47.2 S3.E — Empty state hint: source = vault-files.
    root.innerHTML = `<div class="empty-state">
      <div class="empty-icon">📭</div>
      <div class="empty-title">Karar bulunamadı</div>
      <div class="empty-sub">vault/clients/&lt;slug&gt;/decisions/ altına .md dosyası eklendiğinde (frontmatter type: customer-decision) burada görünür.</div>
    </div>`;
    return;
  }

  const cards = state.kararRows.map(d => {
    const dt = formatDate(d.date);
    const classLabel = CLASS_LABELS[d.decision_class] || d.decision_class;
    const statusLabel = STATUS_LABELS[d.status] || d.status;
    return `
      <div class="karar-card" data-id="${escAttr(String(d.id))}" data-status="${escAttr(d.status)}">
        <div class="karar-date">
          <div class="karar-date-day">${escHtml(dt.day)}</div>
          <div class="karar-date-month">${escHtml(dt.month)}</div>
          <div class="karar-date-year">${escHtml(dt.year)}</div>
        </div>
        <div class="karar-body">
          <div class="karar-row1">
            <span class="karar-title">${escHtml(d.title)}</span>
            <span class="karar-class karar-class-${escAttr(d.decision_class)}">${escHtml(classLabel)}</span>
          </div>
          <div class="karar-row2">
            <span class="karar-client">${escHtml(d.client_slug)}</span>
            <span class="karar-owner">@${escHtml(d.owner_agent || '—')}</span>
            <span class="karar-status karar-status-${escAttr(d.status)}">${escHtml(statusLabel)}</span>
          </div>
          ${d.summary ? `<div class="karar-summary">${escHtml(d.summary)}</div>` : ''}
        </div>
      </div>
    `;
  }).join('');

  root.innerHTML = `<div class="karar-timeline">${cards}</div>`;
}
